import React, { useState } from "react";
import { useQuote } from "../context/QuoteContext";
import { formatEur } from "../utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Plus, Minus, Wrench, ChevronDown } from "lucide-react";
import { Textarea } from "./ui/textarea";

export default function SmontaggioSection() {
  const { ritiro, setRitiro, smontaggioTotal, smontaggioMinutes, items } = useQuote();
  const [open, setOpen] = useState(true);
  const [manualCosto, setManualCosto] = useState("");
  const [manualMin, setManualMin] = useState("");

  const sm = ritiro.smontaggio || {};
  const upd = (patch) => setRitiro((r) => ({ ...r, smontaggio: { ...r.smontaggio, ...patch } }));

  const tar = sm.tariffaOraria ?? 25;
  const persone = sm.numPersone || 1;
  const detailRows = (items || [])
    .filter((it) => it.includeSmont !== false && (it.smontMinutes ?? it.imbMinutes ?? 0) > 0)
    .map((it) => {
      const minTot = (it.smontMinutes ?? it.imbMinutes ?? 0) * it.qty;
      return { id: it.id, tipo: it.tipo, qty: it.qty, minTot, cost: (minTot / 60) * tar * persone };
    });

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="smontaggio-section">
      <div className="flex items-center justify-between p-6 pb-4">
        <div className="flex items-center gap-3">
          <Wrench className="h-5 w-5 text-[#1a5f5a]" />
          <h3 className="font-display font-semibold text-lg">Smontaggio</h3>
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!sm.enabled} onCheckedChange={(v) => upd({ enabled: v })} data-testid="smontaggio-toggle" />
          <button onClick={() => setOpen(!open)} className="text-slate-400"><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
        </div>
      </div>

      {open && sm.enabled && (
        <div className="px-6 pb-6 space-y-4">
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <Label className="text-xs">Numero di persone</Label>
              <div className="flex items-center gap-2 mt-1">
                <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => upd({ numPersone: Math.max(1, (sm.numPersone || 1) - 1) })}><Minus className="h-3.5 w-3.5" /></Button>
                <span className="w-8 text-center font-semibold" data-testid="smontaggio-persone">{sm.numPersone || 1}</span>
                <Button size="icon" variant="outline" className="h-9 w-9" onClick={() => upd({ numPersone: Math.min(5, (sm.numPersone || 1) + 1) })}><Plus className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            <div>
              <Label className="text-xs">Tariffa oraria (€)</Label>
              <Input type="number" value={sm.tariffaOraria ?? 25} onChange={(e) => upd({ tariffaOraria: parseFloat(e.target.value) || 0 })} className="mt-1" data-testid="smontaggio-tariffa-input" />
            </div>
          </div>

          <div className="bg-slate-50 rounded-md p-4 text-sm space-y-1">
            <div className="flex justify-between text-slate-500">
              <span>Tempo smontaggio {sm.isMinutiManual ? "(manuale)" : "(dagli oggetti)"}</span>
              <span>{smontaggioMinutes} min</span>
            </div>
            <div className="flex justify-between font-semibold text-slate-800 pt-2 border-t border-slate-200 mt-2">
              <span>Costo smontaggio</span>
              <span className="text-[#1a5f5a]" data-testid="smontaggio-total">€{formatEur(smontaggioTotal)}</span>
            </div>
          </div>

          {detailRows.length > 0 && (
            <div className="border border-slate-100 rounded-md divide-y divide-slate-100" data-testid="smontaggio-detail">
              <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-slate-400 flex justify-between">
                <span>Dettaglio per oggetto</span><span>minuti · costo</span>
              </div>
              {detailRows.map((r) => (
                <div key={r.id} className="flex items-center justify-between px-3 py-1.5 text-xs" data-testid={`smontaggio-item-${r.id}`}>
                  <span className="text-slate-700 truncate">{r.tipo}{r.qty > 1 ? ` ×${r.qty}` : ""}</span>
                  <span className="text-slate-500 whitespace-nowrap ml-2">{r.minTot} min · <span className="text-[#1a5f5a] font-medium">€{formatEur(r.cost)}</span></span>
                </div>
              ))}
              {(sm.isMinutiManual || sm.isCostoManual) && (
                <div className="px-3 py-1.5 text-[10px] text-amber-600">Dettaglio basato sul calcolo automatico; è attivo un override manuale sul totale.</div>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-2 items-center">
            <Input type="number" placeholder="Minuti manuali" value={manualMin} onChange={(e) => setManualMin(e.target.value)} className="w-40 h-9" />
            <Button size="sm" variant="outline" onClick={() => upd({ isMinutiManual: true, minutiManuale: parseFloat(manualMin) || 0 })} data-testid="smontaggio-manual-min">Imposta minuti</Button>
            {sm.isMinutiManual && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => upd({ isMinutiManual: false, minutiManuale: null })}>Auto</Button>}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Input type="number" placeholder="Costo manuale €" value={manualCosto} onChange={(e) => setManualCosto(e.target.value)} className="w-40 h-9" />
            <Button size="sm" variant="outline" onClick={() => upd({ isCostoManual: true, costoManuale: parseFloat(manualCosto) || 0 })} data-testid="smontaggio-manual-cost">Imposta costo</Button>
            {sm.isCostoManual && <Button size="sm" variant="ghost" className="text-slate-400" onClick={() => upd({ isCostoManual: false, costoManuale: null })}>Auto</Button>}
          </div>

          <div>
            <Label className="text-xs">Nota / disclaimer (in corsivo nel messaggio)</Label>
            <Textarea value={sm.disclaimer ?? ""} onChange={(e) => upd({ disclaimer: e.target.value })}
              rows={3} className="mt-1 text-sm italic" data-testid="smontaggio-disclaimer-input" />
          </div>

          <p className="text-[11px] text-slate-400">
            Il tempo è calcolato dai minuti di smontaggio impostati su ogni oggetto (× quantità), considerando solo gli oggetti con "Includi" attivo. Se attiva, questa voce compare come riga separata nel messaggio WhatsApp.
          </p>
        </div>
      )}
    </div>
  );
}
